import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getFinancesDocumentsThunk } from '../../store/thunks/finances';
import { createFullCourseInvoice } from '../../api/finances/index';
import actions from '../../store/actions';
import FinanceCourseCard from './FinanceCourseCard';
import TrialFinanceCard from './TrialFinanceCard';
import Plug from '../plug/plug.component';
import { ChevronDown } from './icons';
import { DEMO_COURSES, isDemoProduct } from '../../utils/financeDemo';

import './finances-v2.scss';

const ACTIVE = [1, 3];

const Section = ({ title, count, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className={`v2-section ${open ? '' : 'is-closed'}`.trim()}>
      <button type="button" className="v2-section__head" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <h2 className="v2-section__title">{title}{count !== null ? ` (${count})` : ''}</h2>
        <ChevronDown className="v2-section__chev" />
      </button>
      {open && children}
    </section>
  );
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

Section.defaultProps = { count: null, defaultOpen: true };

const rememberCourse = (course) => {
  localStorage.setItem('finance_course', JSON.stringify({
    product_id: course.product_id,
    name: course.name,
    direction_name: course.direction_name,
    program_name: course.program_name,
    type: course.type,
    image: course.flag || course.image,
    lessons_total: course.lessons_total,
    lessons_passed: course.lessons_passed,
    lessons_left: course.lessons_left,
    is_demo: Boolean(course.is_demo),
  }));
};

const FinancesV2 = ({ courses, trialCourses, token, isTestStudent, setModalOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState('');

  const list = useMemo(
    () => (courses.length === 0 && trialCourses.length === 0 && isTestStudent ? DEMO_COURSES : courses),
    [courses, trialCourses, isTestStudent]
  );

  const active = list.filter((course) => ACTIVE.includes(Number(course.status)));
  const inactive = list.filter((course) => !ACTIVE.includes(Number(course.status)));

  const openAccounts = (course) => {
    rememberCourse(course);
    localStorage.setItem('product_id', JSON.stringify(course.product_id));
    localStorage.setItem('type', JSON.stringify('course'));
    navigate('/finance/accounts');
  };

  const openTrialAccounts = (course) => {
    localStorage.setItem('product_id', JSON.stringify(course.product_id));
    localStorage.setItem('type', JSON.stringify('trial_courses'));
    navigate('/finance/accounts');
  };

  const openHistory = (course) => {
    if (isDemoProduct(course.product_id)) {
      setError('Це показовий курс тестового студента — історії оплат у нього немає.');
      return;
    }

    setModalOpen('open');
    dispatch(getFinancesDocumentsThunk({ product_id: course.product_id, token }));
  };

  const payFullCourse = async (course) => {
    setError('');
    rememberCourse(course);
    localStorage.setItem('product_id', JSON.stringify(course.product_id));
    localStorage.setItem('type', JSON.stringify('course'));

    if (isDemoProduct(course.product_id)) {
      localStorage.setItem('invoice_id', JSON.stringify('demo-actual'));
      navigate('/finance/accounts/pay');
      return;
    }

    setLoadingId(course.product_id);
    try {
      const res = await createFullCourseInvoice({ product_id: course.product_id, token });
      const invoiceId = res?.data?.data?.invoice_id;
      if (!invoiceId) {
        throw new Error('no-invoice');
      }
      dispatch(actions.finances.getPayActulInvoice({}));
      localStorage.setItem('invoice_id', JSON.stringify(invoiceId));
      navigate('/finance/accounts/pay');
    } catch (e) {
      const message = e?.response?.data?.info?.message;
      setError((message && (message.message || message)) || 'Не вдалося створити рахунок. Спробуйте ще раз або напишіть менеджеру.');
    } finally {
      setLoadingId(null);
    }
  };

  if (list.length === 0 && trialCourses.length === 0) {
    return <Plug plug="Дані про фінанси відсутні!" icon="file-closed" />;
  }

  const renderCards = (items) => (
    <div className="v2-section__body">
      {items.map((course) => (
        <FinanceCourseCard
          key={course.product_id}
          course={course}
          onAccounts={() => openAccounts(course)}
          onHistory={() => openHistory(course)}
          onFullCourse={() => payFullCourse(course)}
          fullCourseLoading={loadingId === course.product_id}
        />
      ))}
    </div>
  );

  return (
    <div className="fv2">
      {error && <div className="fv2__error" role="alert">{error}</div>}

      {trialCourses.length > 0 && (
        <Section title="Пробні">
          <div className="v2-section__body">
            {trialCourses.map((course) => (
              <TrialFinanceCard key={course.product_id} course={course} onPay={() => openTrialAccounts(course)} />
            ))}
          </div>
        </Section>
      )}

      {active.length > 0 && <Section title="В навчанні">{renderCards(active)}</Section>}

      {inactive.length > 0 && (
        <Section title="Неактивні" count={inactive.length} defaultOpen={false}>{renderCards(inactive)}</Section>
      )}
    </div>
  );
};

FinancesV2.propTypes = {
  courses: PropTypes.array.isRequired,
  trialCourses: PropTypes.array.isRequired,
  token: PropTypes.string,
  isTestStudent: PropTypes.bool,
  setModalOpen: PropTypes.func.isRequired,
};

FinancesV2.defaultProps = {
  token: '',
  isTestStudent: false,
};

export default FinancesV2;
